/*
 * Copyright (C) 2020 Reciprocity, Inc - All Rights Reserved
 * Unauthorized use, copying, distribution, displaying, or public performance
 * of this file, via any medium, is strictly prohibited. All information
 * contained herein is proprietary and confidential and may not be shared
 * with any third party without the express written consent of Reciprocity, Inc.
 */

import axios, { AxiosRequestConfig, AxiosResponse, AxiosError, Canceler } from 'axios';
import { apiUrl } from '@/env';

axios.defaults.baseURL = apiUrl;

/**
 * The following request functions will be the layer that is responsible for actually
 * making the request to the server. They act as wrappers around the Axios client and
 * handle each HTTP verb.
 */
export const getRequest = <T = any>(config: ZenRequestConfig<T>) =>
  sendRequest<T>(requestFactory<T>(HTTPVerb.GET, config), config.resolver);

export const postRequest = <T = any, K = any>(config: ZenRequestConfig<T>) =>
  sendRequest<K>(requestFactory<T>(HTTPVerb.POST, config), config.resolver);

export const putRequest = <T = any, K = any>(config: ZenRequestConfig<T>) =>
  sendRequest<K>(requestFactory<T>(HTTPVerb.PUT, config), config.resolver);

export const patchRequest = <T = any, K = any>(config: ZenRequestConfig<T>) =>
  sendRequest<K>(requestFactory<T>(HTTPVerb.PATCH, config), config.resolver);

export const deleteRequest = (config: ZenRequestConfig<void>) =>
  sendRequest<void>(requestFactory<void>(HTTPVerb.DELETE, config), config.resolver);

/**
 * Responisble for actually making the request to the server.
 * @param config AxiosRequestConfig
 * @param resolver Function (optional) If is defined, it will be called after the promise resolves.
 */
export const sendRequest = <T = any>(config: AxiosRequestConfig, resolver?: Function): Promise<T> =>
  axios
    .request<T>({
      ...config,
      withCredentials: true,
    })
    .then((response: AxiosResponse) => {
      const resolve = resolver ? resolver(response) : response.data;
      return Promise.resolve(resolve);
    })
    .catch((error: AxiosError) => Promise.reject(error as ZenError));

/**
 * It creates and returns a AxiosRequestConfig with the provided method and config
 * @param method HTTPVerb
 * @param config ZenRequestConfig
 */
export function requestFactory<T = any>(method: HTTPVerb, config: ZenRequestConfig<T>) {
  const { resolver, cancel, ...axiosRequest } = config;

  const request: AxiosRequestConfig = {
    ...axiosRequest,
    method,
  };

  if (cancel) {
    request.cancelToken = new axios.CancelToken(cancel);
  }

  return request;
}

/**
 *  This interface extends the AxiosRequestConfig one with custom properties.
 *  Properties:
  
    url?: string;
    method?: string;
    baseURL?: string;
    transformRequest?: AxiosTransformer | AxiosTransformer[];
    transformResponse?: AxiosTransformer | AxiosTransformer[];
    headers?: any;
    params?: any;
    paramsSerializer?: (params: any) => string;
    data?: any;
    timeout?: number;
    withCredentials?: boolean;
    adapter?: AxiosAdapter;
    auth?: AxiosBasicCredentials;
    responseType?: string;
    xsrfCookieName?: string;
    xsrfHeaderName?: string;
    onUploadProgress?: (progressEvent: any) => void;
    onDownloadProgress?: (progressEvent: any) => void;
    maxContentLength?: number;
    validateStatus?: (status: number) => boolean;
    maxRedirects?: number;
    httpAgent?: any;
    httpsAgent?: any;
    proxy?: AxiosProxyConfig | false;
    cancelToken?: CancelToken;
    resolver?: Function;
 */
export interface ZenRequestConfig<T = any> extends AxiosRequestConfig {
  data?: T;
  /** If is defined, it will be called after the promise resolves */
  resolver?: Function;
  /** If is defined, it represents the executor of the axios cancel token */
  cancel?: (cancel: Canceler) => void;
}

/**
 *  This interface just extends the AxiosResponse.
 *  Properties:
  
    data: T;
    status: number;
    statusText: string;
    headers: any;
    config: AxiosRequestConfig;
    request?: any;
 */
export interface ZenResponse<T = any> extends AxiosResponse<T> {}

/**
 *  This interface just extends the AxiosError.
 *  Properties:
  
    config: AxiosRequestConfig;
    code?: string;
    request?: any;
    response?: AxiosResponse;
 */
export interface ZenError extends AxiosError {}

/**
 * Http verbs
 * @readonly
 * @enum {string}
 * GET | POST | PUT | PATCH | DELETE
 */
export enum HTTPVerb {
  GET = 'GET',
  POST = 'POST',
  PUT = 'PUT',
  PATCH = 'PATCH',
  DELETE = 'DELETE',
}
